// 2D affine matrix in SVG order: [a c e; b d f; 0 0 1].
export interface Matrix {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

export const IDENTITY: Matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

export const multiply = (m: Matrix, n: Matrix): Matrix => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f,
});

export const translate = (x: number, y: number): Matrix => ({ ...IDENTITY, e: x, f: y });
export const scale = (x: number, y: number = x): Matrix => ({ ...IDENTITY, a: x, d: y });

export const rotate = (degrees: number, cx = 0, cy = 0): Matrix => {
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const r: Matrix = { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
  return cx || cy ? multiply(multiply(translate(cx, cy), r), translate(-cx, -cy)) : r;
};

export const apply = (m: Matrix, p: Point): Point => ({
  x: m.a * p.x + m.c * p.y + m.e,
  y: m.b * p.x + m.d * p.y + m.f,
});

const NUMBER_RE = /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g;

export const parseNumberList = (value: string): number[] => (value.match(NUMBER_RE) ?? []).map(Number);

const TRANSFORM_RE = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;

const toMatrix = (kind: string, args: number[]): Matrix | null => {
  const [p0 = 0, p1, p2 = 0, p3 = 0, p4 = 0, p5 = 0] = args;
  switch (kind) {
    case "matrix":
      return args.length === 6 ? { a: p0, b: p1 ?? 0, c: p2, d: p3, e: p4, f: p5 } : null;
    case "translate":
      return translate(p0, p1 ?? 0);
    case "scale":
      return scale(p0, p1 ?? p0);
    case "rotate":
      return rotate(p0, p1 ?? 0, p2);
    case "skewX":
      return { ...IDENTITY, c: Math.tan((p0 * Math.PI) / 180) };
    case "skewY":
      return { ...IDENTITY, b: Math.tan((p0 * Math.PI) / 180) };
    default:
      return null;
  }
};

/** Parses an SVG transform attribute. Unknown or malformed parts are ignored. */
export const parseTransform = (value: string | undefined): Matrix => {
  if (!value) return IDENTITY;
  let result = IDENTITY;
  TRANSFORM_RE.lastIndex = 0;
  for (let match = TRANSFORM_RE.exec(value); match; match = TRANSFORM_RE.exec(value)) {
    const next = toMatrix(match[1], parseNumberList(match[2]));
    if (next && Object.values(next).every(Number.isFinite)) result = multiply(result, next);
  }
  return result;
};

export interface Decomposed {
  readonly rotation: number; // radians
  readonly scaleX: number;
  readonly scaleY: number;
}

const ORTHOGONAL_EPSILON = 1e-6;

/** Splits a matrix into rotation + axis scales, or null when it has skew or a mirror. */
export const decompose = (m: Matrix): Decomposed | null => {
  const scaleX = Math.hypot(m.a, m.b);
  const scaleY = Math.hypot(m.c, m.d);
  if (scaleX === 0 || scaleY === 0) return null;
  const dot = (m.a * m.c + m.b * m.d) / (scaleX * scaleY);
  const det = m.a * m.d - m.b * m.c;
  if (Math.abs(dot) > ORTHOGONAL_EPSILON || det <= 0) return null;
  return { rotation: Math.atan2(m.b, m.a), scaleX, scaleY };
};

/** Mean linear scale of a matrix, used for stroke widths and font sizes. */
export const meanScale = (m: Matrix): number => Math.sqrt(Math.abs(m.a * m.d - m.b * m.c));
